/**
 * Simple interface to animate details elements as an accordion.
 *
 * Usage:
 * <div class="jm-accordion" data-allow-multiple="true" data-animation-speed="500">
 *  <details>
 * 	 <summary>Some summary</summary>
 * 	 <div>
 * 	  <p>Some content</p>
 * 	 </div>
 *  </details>
 * </div>
 *
 * Modified from:
 * https://css-tricks.com/how-to-animate-the-details-element-using-waapi/
 */

 (function () {
  /**
   * Check if browser has web animations API
   * 
   * @returns {boolean}
   */
  function canAnimate() {
    return 'animate' in HTMLElement.prototype;
  }
  
	/**
	 * @typedef {(AccordionElement: accordion) => void;} AccordionElementCallback
	 */

	/**
	 * Wrapper for single accordion element (<details> element)
	 */
	class AccordionElement {
		/**
		 * A single accordion item (<detail> element)
		 *
		 * @param {HTMLDetailsElement} detailsElement
		 * @param {{
		 *  onOpening?: AccordionElementCallback;
		 *  onOpen?: AccordionElementCallback;
		 *  onClosing?: AccordionElementCallback;
		 *  onClose?: AccordionElementCallback;
		 *  animationSpeed: number;
		 * }} options
		 */
		constructor(detailsElement, options) {
			this.detailsElement = detailsElement;
			this.summaryElement = detailsElement.querySelector('summary');
			this.contentElement = this.summaryElement.nextElementSibling;
			this.animation = null;
			this.isClosing = false;
			this.isExpanding = false;
			this.onOpening = options.onOpening || null;
			this.onOpen = options.onOpen || null;
			this.onClosing = options.onClosing || null;
			this.onClose = options.onClose || null;
			this.animationSpeed = options.animationSpeed || 300;
			this.summaryElement.addEventListener('click', this.onClick.bind(this));
		}

		onClick(event) {
			event.preventDefault();
			if (this.isClosing || !this.detailsElement.open) {
				this.open();
			} else if (this.isExpanding || this.detailsElement.open) {
				this.close();
			}
		}

		isOpen() {
			return this.detailsElement.open || this.isExpanding;
		}

		open() {
			this.detailsElement.style.height = `${this.detailsElement.offsetHeight}px`;
			this.detailsElement.style.overflow = 'hidden';
			this.detailsElement.open = true;
			if (this.onOpening) {
				this.onOpening(this);
			}
      if(canAnimate()) {
        window.requestAnimationFrame(this.expand.bind(this));
      } else {
        this.onAnimationFinish(true);
      }
		}

		expand() {
			const startHeight = `${this.detailsElement.offsetHeight}px`;
			const endHeight = `${this.summaryElement.offsetHeight + this.contentElement.offsetHeight}px`;
      
      // Set attributes and animate open
			if (this.animation) {
				this.animation.cancel();
			}
			this.isExpanding = true;
			this.detailsElement.setAttribute('is-opening', '');
			this.animation = this.detailsElement.animate(
				{
					height: [startHeight, endHeight],
				},
				{
					duration: this.animationSpeed,
					easing: 'ease-out',
				}
			);
			this.animation.onfinish = () => this.onAnimationFinish(true);
			this.animation.oncancel = () => {
				this.detailsElement.removeAttribute('is-opening');
				this.isExpanding = false;
			};
		}

		close() {
      // Trigger close
			this.detailsElement.style.overflow = 'hidden';
			if (this.onClosing) {
				this.onClosing(this);
			}
      if(canAnimate()) {
        window.requestAnimationFrame(this.shrink.bind(this));
      } else {
        this.onAnimationFinish(false);
      }
		}

		shrink() {
			const startHeight = `${this.detailsElement.offsetHeight}px`;
			const endHeight = `${this.summaryElement.offsetHeight}px`;

      // Cancel any current animation
			if (this.animation) {
				this.animation.cancel();
			}
      // Set attributes and animate close
			this.isClosing = true;
			this.detailsElement.setAttribute('is-closing', '');
			this.animation = this.detailsElement.animate(
				{
					height: [startHeight, endHeight],
				},
				{
					duration: this.animationSpeed,
					easing: 'ease-out',
				}
			);
			this.animation.onfinish = () => this.onAnimationFinish(false);
			this.animation.oncancel = () => {
				this.detailsElement.removeAttribute('is-closing');
				this.isClosing = false;
			};
		}

		onAnimationFinish(isOpen) {
			this.detailsElement.open = isOpen;
      
      // Tidy up
			this.animation = null;
			this.isClosing = false;
			this.isExpanding = false;
			this.detailsElement.style.height = '';
			this.detailsElement.style.overflow = '';
      
			this.detailsElement.removeAttribute('is-opening');
			this.detailsElement.removeAttribute('is-closing');
      
			if (isOpen) {
				if (this.onOpen) {
					this.onOpen(this);
				}
			} else {
				if (this.onClose) {
					this.onClose(this);
				}
			}
		}
	}

	/**
	 * Wrapper for multiple accordion elements (<details> elements)
	 */
	class Accordion {
		/**
		 * A single accordion item (<detail> element)
		 *
		 * @param {HTMLElement} parentElement
		 * @param {{
		 *   allowMultiple?: boolean;
		 *   animationSpeed?: number;
		 * }} options
		 */
		constructor(parentElement, options) {
			this.allowMultiple = !!options.allowMultiple;
			this.animationSpeed = options.animationSpeed || 300;

			const detailsElements = [...parentElement.querySelectorAll('details')];
			this.children = detailsElements.map(
				detailsElement =>
					new AccordionElement(detailsElement, {
						onOpening: this.handleChildOpening.bind(this),
						animationSpeed: this.animationSpeed,
					})
			);
		}

		handleChildOpening(accordion) {
			if (!this.allowMultiple) {
				this.children.forEach(child => {
					if (child !== accordion && child.isOpen()) {
						child.close();
					}
				});
			}
		}
	}

	window.addEventListener('DOMContentLoaded', function () {
		document.querySelectorAll('.jm-accordion').forEach(
			accordion =>
				new Accordion(accordion, {
					allowMultiple: accordion.dataset.allowMultiple === 'true',
					animationSpeed: accordion.dataset.animationSpeed
						? parseInt(accordion.dataset.animationSpeed)
						: 300,
				})
		);
	});
})();
